import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Megaphone, Heart, MessageCircle, BarChart3, Eye } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError, formatCount } from "@/lib/api";
import { SponsorReportDialog } from "@/components/super/SponsorReportDialog";

function SponsorRow({ s, onSaved, onReport }) {
  const [f, setF] = useState({ sponsored: !!s.sponsored, priority: s.sponsor_priority || 5, until: s.sponsor_until ? s.sponsor_until.slice(0, 10) : "" });
  const [busy, setBusy] = useState(false);
  const save = async (next = f) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/super/shorts/${s.id}/sponsor`, { ...next, until: next.until || null });
      toast.success(next.sponsored ? "Curta patrocinado atualizado" : "Patrocínio removido");
      onSaved({ ...s, ...data });
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const toggle = (v) => { const n = { ...f, sponsored: v }; setF(n); save(n); };

  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-2xl border p-3 ${s.sponsored_active ? "border-[hsl(var(--gold))]/50 bg-[hsl(var(--gold))]/5" : "border-white/5 bg-card"}`} data-testid={`super-short-${s.id}`}>
      <img src={s.poster_url} alt="" className="h-20 w-12 shrink-0 rounded-lg object-cover" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold">{s.caption}</p>
        <p className="truncate text-xs text-muted-foreground">{s.author_name} · <Heart size={11} className="inline" /> {formatCount(s.likes)} · <MessageCircle size={11} className="inline" /> {formatCount(s.comments_count)}</p>
        {s.sponsored && !s.sponsored_active && <p className="text-[11px] text-amber-300">Patrocínio expirado</p>}
        {s.campaign_totals && <p className="text-[11px] font-semibold text-[hsl(var(--gold))]" data-testid={`super-short-campaign-${s.id}`}><Eye size={11} className="inline" /> {formatCount(s.campaign_totals.views)} visualizações · {s.campaign_totals.likes} curtidas na campanha</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {(s.sponsor_campaign || s.campaign_totals) && <Button size="sm" variant="secondary" className="rounded-full" onClick={() => onReport(s.id)} data-testid={`super-short-report-${s.id}`}><BarChart3 size={14} className="mr-1" />Relatório</Button>}
        {f.sponsored && <>
          <label className="flex items-center gap-1 text-xs text-muted-foreground">Prioridade
            <Input type="number" min={1} max={10} value={f.priority} onChange={(e) => setF({ ...f, priority: Math.max(1, Math.min(10, Number(e.target.value) || 1)) })} className="h-9 w-16 rounded-lg" data-testid={`super-short-priority-${s.id}`} /></label>
          <label className="flex items-center gap-1 text-xs text-muted-foreground">Até
            <Input type="date" value={f.until} onChange={(e) => setF({ ...f, until: e.target.value })} className="h-9 w-36 rounded-lg" data-testid={`super-short-until-${s.id}`} /></label>
          <Button size="sm" variant="secondary" disabled={busy} className="rounded-full" onClick={() => save()} data-testid={`super-short-save-${s.id}`}>Salvar</Button>
        </>}
        <label className="flex items-center gap-2 text-xs font-bold"><Megaphone size={14} className="text-[hsl(var(--gold))]" />Patrocinado
          <Switch checked={f.sponsored} disabled={busy} onCheckedChange={toggle} data-testid={`super-short-sponsor-toggle-${s.id}`} /></label>
      </div>
    </div>
  );
}

export function SponsoredPanel() {
  const [q, setQ] = useState("");
  const [only, setOnly] = useState(false);
  const [items, setItems] = useState(null);
  const [report, setReport] = useState(null);
  const load = useCallback(() => api.get("/super/shorts", { params: { q, sponsored: only } }).then((r) => setItems(r.data.items)).catch((e) => toast.error(formatApiError(e))), [q, only]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const saved = (s) => setItems((p) => p.map((x) => (x.id === s.id ? s : x)));

  return (
    <div className="space-y-4" data-testid="super-sponsored-panel">
      <p className="text-sm text-muted-foreground">Os curtas patrocinados aparecem primeiro em "Para você", por ordem de prioridade (10 = máxima), e voltam a aparecer a cada 5 curtas, com a etiqueta "Patrocinado".</p>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[200px] flex-1"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por legenda ou autor" className="h-10 rounded-full bg-white/5 pl-9" data-testid="super-shorts-search" /></div>
        <label className="flex items-center gap-2 text-sm"><Switch checked={only} onCheckedChange={setOnly} data-testid="super-shorts-only-sponsored" />Só patrocinados</label>
      </div>
      <div className="space-y-2">{items?.map((s) => <SponsorRow key={s.id} s={s} onSaved={saved} onReport={setReport} />)}</div>
      {report && <SponsorReportDialog shortId={report} onClose={() => setReport(null)} />}
      {items?.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground">Nenhum curta encontrado.</p>}
    </div>
  );
}
